import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';

interface VoiceMessagePlayerProps {
  url: string;
  duration?: number;
  isGlobalDarkMode: boolean;
  isOwn?: boolean;
}

const formatTime = (secs: number) => {
  if (!isFinite(secs) || isNaN(secs)) return '0:00';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const VoiceMessagePlayer: React.FC<VoiceMessagePlayerProps> = ({
  url,
  duration: storedDuration,
  isGlobalDarkMode,
  isOwn = false,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(storedDuration ?? 0);
  const [speed, setSpeed] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragTime, setDragTime] = useState(0);

  // Sync storedDuration if it changes
  useEffect(() => {
    if (storedDuration && storedDuration > 0) {
      setDuration(storedDuration);
    }
  }, [storedDuration]);

  // Load duration when metadata is ready (with WebM Infinity workaround)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    const onLoaded = () => {
      if (isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      } else if (!storedDuration) {
        // Workaround for WebM Infinity duration bug in Chrome
        audio.currentTime = 1e8; 
        const onSeeked = () => {
          audio.removeEventListener('seeked', onSeeked);
          audio.currentTime = 0;
          if (isFinite(audio.duration) && audio.duration > 0) {
            setDuration(audio.duration);
          }
        };
        audio.addEventListener('seeked', onSeeked);
      }
    };
    
    audio.addEventListener('loadedmetadata', onLoaded);
    return () => audio.removeEventListener('loadedmetadata', onLoaded);
  }, [url, storedDuration]);

  // Sync current time to state
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => {
      if (!isDragging) {
        setCurrentTime(audio.currentTime);
      }
    };
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('ended', onEnded);
    };
  }, [isDragging]);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.playbackRate = speed;
      await audio.play().catch(console.error);
      setIsPlaying(true);
    }
  }, [isPlaying, speed]);

  const handleReplay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = 0;
    audio.playbackRate = speed;
    audio.play().catch(console.error);
    setIsPlaying(true);
  }, [speed]);

  const cycleSpeed = useCallback(() => {
    const speeds = [1, 1.5, 2];
    const next = speeds[(speeds.indexOf(speed) + 1) % speeds.length];
    setSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  }, [speed]);

  const handleSeekChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    setDragTime(val);
    setCurrentTime(val);
    if (audioRef.current) {
      audioRef.current.currentTime = val;
    }
  };

  const handleSeekStart = () => {
    setIsDragging(true);
    setDragTime(currentTime);
  };

  const handleSeekEnd = () => {
    setIsDragging(false);
  };

  const displayTime = isDragging ? dragTime : currentTime;
  const effectiveDuration = Math.max(duration, displayTime);
  const progress = effectiveDuration > 0 ? Math.min(Math.max(displayTime / effectiveDuration, 0), 1) : 0;
  const hasEnded = !isPlaying && currentTime > 0 && currentTime >= effectiveDuration - 0.1;

  // Theming definitions
  const bgColor = isOwn
    ? (isGlobalDarkMode ? 'bg-workspace-accent/20' : 'bg-workspace-accent/10')
    : (isGlobalDarkMode ? 'bg-card' : 'bg-gray-100');
  
  const btnColor = isOwn
    ? 'bg-workspace-accent text-white hover:bg-workspace-accent/90 shadow-sm'
    : (isGlobalDarkMode ? 'bg-muted text-foreground hover:bg-accent shadow-sm' : 'bg-white text-gray-700 hover:bg-gray-50 shadow-sm border border-gray-200');
  
  const trackActiveColor = isOwn ? 'bg-workspace-accent' : (isGlobalDarkMode ? 'bg-foreground' : 'bg-gray-600');
  const trackInactiveColor = isOwn
    ? (isGlobalDarkMode ? 'bg-workspace-accent/30' : 'bg-workspace-accent/20')
    : (isGlobalDarkMode ? 'bg-foreground/20' : 'bg-gray-300');
  const thumbColor = isOwn ? 'bg-workspace-accent' : (isGlobalDarkMode ? 'bg-foreground' : 'bg-gray-600');
  
  const textColor = isOwn 
    ? 'text-workspace-accent' 
    : (isGlobalDarkMode ? 'text-muted-foreground' : 'text-gray-500');

  return (
    <div className={cn('flex items-center gap-3 rounded-2xl px-3.5 py-3 w-full min-w-[260px] sm:min-w-[340px] max-w-[380px]', bgColor)}>
      <audio ref={audioRef} src={url} preload="metadata" />

      {/* Play / Pause / Replay button */}
      <button
        onClick={hasEnded ? handleReplay : togglePlay}
        className={cn(
          'shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200',
          btnColor
        )}
        title={hasEnded ? 'Replay' : isPlaying ? 'Pause' : 'Play'}
      >
        {hasEnded ? (
          <RotateCcw className="w-4 h-4" />
        ) : isPlaying ? (
          <Pause className="w-4 h-4 fill-current" />
        ) : (
          <Play className="w-4 h-4 fill-current translate-x-0.5" />
        )}
      </button>

      {/* Track + progress container */}
      <div className="flex-1 flex flex-col gap-1.5 min-w-0">
        
        {/* Seek Track */}
        <div className="relative flex items-center h-6 w-full group">
          
          {/* Background track (inactive) */}
          <div className={cn("absolute inset-x-0 h-1.5 rounded-full overflow-hidden pointer-events-none", trackInactiveColor)}>
            {/* Filled track (active) */}
            <div 
              className={cn("h-full", trackActiveColor)} 
              style={{ width: `${progress * 100}%` }}
            />
          </div>
          
          {/* Draggable Thumb (visual only) */}
          <div 
            className={cn(
              "absolute h-3.5 w-3.5 rounded-full shadow-sm pointer-events-none transition-transform group-hover:scale-125 z-20", 
              thumbColor
            )}
            style={{ left: `calc(${progress * 100}% - 7px)` }}
          />

          {/* Native Range Input for flawless native seeking (invisible) */}
          <input
            type="range"
            min="0"
            max={effectiveDuration || 100}
            step="0.01"
            value={isDragging ? dragTime : currentTime}
            onChange={handleSeekChange}
            onPointerDown={handleSeekStart}
            onPointerUp={handleSeekEnd}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10 touch-none"
            title="Seek"
          />
        </div>

        {/* Timestamps & Speed control */}
        <div className="flex items-center justify-between">
          <span className={cn('text-[11px] font-mono tabular-nums font-medium', textColor)}>
            {isPlaying || currentTime > 0 ? formatTime(displayTime) : formatTime(duration)}
          </span>
          <button
            onClick={cycleSpeed}
            className={cn(
              'text-[10px] font-bold px-1.5 py-0.5 rounded transition-colors',
              isOwn
                ? (isGlobalDarkMode ? 'text-workspace-accent hover:bg-workspace-accent/20' : 'text-workspace-accent hover:bg-workspace-accent/10')
                : (isGlobalDarkMode ? 'text-muted-foreground hover:bg-accent' : 'text-gray-500 hover:bg-gray-200')
            )}
            title="Playback speed"
          >
            {speed}×
          </button>
        </div>
      </div>
    </div>
  );
};

export default VoiceMessagePlayer;
